import { DatePipe, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';

import {
  InsurancePackage,
  InsurancePackageService,
  InsurancePackagesState,
  PackageClientType,
  PackageStatus,
  PackageType,
  deleteInsurancePackage,
  updateInsurancePackage,
} from '@lib/insurance-packages/data-access';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { take } from 'rxjs';

import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';

import { InfoBadgeColors, InfoBadgeComponent } from '@lib/shared-ui/commons/info-badge/info-badge.component';
import { StatusComponent } from '@lib/shared-ui/commons/status/status.component';

@Component({
  selector: 'insurance-packages-item',
  standalone: true,
  imports: [InfoBadgeComponent, StatusComponent, MatIcon, MatMenu, MatMenuTrigger, MatMenuItem, NgOptimizedImage, DatePipe, TranslatePipe],
  templateUrl: './insurance-package-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InsurancePackageItemComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly store = inject(Store<InsurancePackagesState>);
  private readonly packageService = inject(InsurancePackageService);

  protected readonly colors = InfoBadgeColors;
  protected readonly clientTypes = PackageClientType;
  protected readonly packageTypes = PackageType;
  protected readonly packageStatus = PackageStatus;

  insurancePackage = input.required<InsurancePackage>();

  protected historicalInsurancePackages = signal<InsurancePackage[]>([]);

  protected editInsurancePackage() {
    this.router.navigate(['/insurance-packages-legacy/edit', this.insurancePackage().id]);
  }

  ngOnInit(): void {
    this.getPackagesFromStore();
  }

  private getPackagesFromStore() {
    this.store
      .select('insurance-packages')
      .pipe(take(1))
      .subscribe(res => {
        const packagesHistory = res.insurancePackages.filter(item => item.parentPackageId === this.insurancePackage().id);
        this.historicalInsurancePackages.set(packagesHistory);
      });
  }

  protected activateInsurancePackage() {
    const insurancePackage = this.insurancePackage();
    this.packageService.activateInsurancePackage(insurancePackage.id, insurancePackage).subscribe(updatedPackage => {
      // Update store with the activated package
      this.store.dispatch(
        updateInsurancePackage({
          insurancePackage: updatedPackage || { ...insurancePackage, status: PackageStatus.Active },
        })
      );
    });
  }

  protected deleteEmptyPackage() {
    const insurancePackage = this.insurancePackage();

    return this.packageService
      .deleteEmptyInsurancePackage(insurancePackage.id)
      .pipe(take(1))
      .subscribe(() => {
        // Remove package from store after successful deletion
        this.store.dispatch(deleteInsurancePackage({ id: insurancePackage.id }));
      });
  }
}
